import React, { Fragment } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
  Grid,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import BackupIcon from '@mui/icons-material/Backup';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/_common/models/redux/store';
import { getAlertAction } from 'src/_common/handlers/services';
import { toggleAlertDialogAction } from 'src/_common/actions/commonActions';

const useStyle = makeStyles((theme) => ({
  dialog: {
    maxWidth: '390px',
    margin: '-100px 32px 32px',
    [theme.breakpoints.down(600)]: {
      margin: '32px',
    },
    [theme.breakpoints.down(320)]: {
      marginLeft: 20,
      marginRight: 20,
    },
  },
  dialogTitle: {
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingBottom: '0px',
    fontSize: '20px',
    lineHeight: '1.35',
    fontWeight: 600,
  },
  dialogContent: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  actionBtn: {
    color: theme.specialColors.BLUE_COLOR,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  closeIcon: {
    color: '#FFFFFF',
    fill: '#FFFFFF',
    position: 'absolute',
    top: '4px',
    right: '5px',
  },
}));

function AlertDialog() {
  const classes = useStyle();
  const alertDialog = useSelector((state: RootState) => state.common.alertDialog);
  const dispatch = useDispatch();

  const onClose = () => {
    getAlertAction()?.dialogCloseHandler();
    dispatch(
      toggleAlertDialogAction(
        !!alertDialog?.isBackdropDismissalAllowed,
        `${alertDialog?.content}`,
        `${alertDialog?.title}`,
        false,
        `${alertDialog?.buttonLabel}`
      )
    );
  };

  const handleActionButton = (event: React.SyntheticEvent<{}, Event>) => {
    if (getAlertAction) getAlertAction()?.closeButtonClickHandler(event);

    onClose();
  };

  const getContent = () => {
    if (typeof alertDialog?.content === 'object') {
      return alertDialog?.content.map((el, index) => (
        <Fragment key={`alert-${el}}`}>
          {el}
          {index !== alertDialog.content.length - 1 && <br />}
        </Fragment>
      ));
    }
  };

  const getTitleIcon = () => {
    if (typeof alertDialog?.titleIcon === 'string') {
      switch (alertDialog?.titleIcon) {
        case 'uploadFile':
          return <BackupIcon />;
        case 'downloadFile':
          return <CloudDownloadIcon />;
        default:
          break;
      }
    }
  };

  return (
    <Dialog
      open={!!alertDialog?.isShown}
      classes={{ paperWidthSm: classes.dialog }}
      // disableBackdropClick={!alertDialog?.isBackdropDismissalAllowed}
      disableEscapeKeyDown={!alertDialog?.isBackdropDismissalAllowed}
      // onClose={onClose}
      onClose={(event, reason) => {
        if (alertDialog?.isBackdropDismissalAllowed || reason !== 'backdropClick') {
          onClose();
        }
      }}
      aria-labelledby="alert-box-title"
      aria-describedby="alert-box-description"
    >
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        id="alert-box-title"
        style={
          alertDialog?.isErrorModal
            ? { background: '#F24822', color: ' #FFFFFF', paddingTop: '8px', paddingBottom: '8px' }
            : {}
        }
      >
        {alertDialog?.isErrorModal ? (
          <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
            <Grid item>
              <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item style={{ paddingTop: '6px' }}>
                  {getTitleIcon()}
                </Grid>
                <Grid item style={{ paddingLeft: '14px' }}>
                  {alertDialog?.title}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <CloseIcon classes={{ root: classes.closeIcon }} onClick={onClose} />
            </Grid>
          </Grid>
        ) : (
          alertDialog?.title
        )}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <DialogContentText id="alert-box-description">
          {typeof alertDialog?.content === 'string' && alertDialog.content}
          {typeof alertDialog?.content === 'object' && getContent()}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {alertDialog?.isBackdropDismissalAllowed ? (
          <Button onClick={onClose} classes={{ root: classes.actionBtn }}>
            CANCEL
          </Button>
        ) : null}
        {alertDialog?.buttonLabel && (
          <Button onClick={handleActionButton} classes={{ root: classes.actionBtn }} autoFocus>
            {alertDialog?.buttonLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AlertDialog;
